// ==================== Step3ReadingsAndPayments.tsx ====================
import React, { useState, useCallback, useMemo } from "react";
import { StepHelpers } from "@shared/components/Stepper";
import { Card, CardContent } from "@shared/ui/card";
import { AlertCircle, CalendarIcon, Info, Settings, X } from "lucide-react";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import { Input } from "@shared/ui/input";
import { Badge } from "@shared/ui/badge";
import { cn } from "@shared/lib/utils";
import { Tabs } from "@shared/ui/tabs";
import MeterReadings from "./tabs/MeterReadings";
import PaymentsServiceCharges from "./tabs/PaymentsServiceCharges";
import { normalizeDate } from "@shared/utils/dateUtils";
import { useUpdateConsumerSelection, usePlanData } from "@features/bx/singleBillGenaration/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface Step3ReadingsAndPaymentsProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

interface MeterReading {
  meter_id: string;
  meter_number: string;
  current_reading: number;
  reading_date: string;
  reading_source: number;
  status: string;
  product_code: string;
}

interface MeterReadingPayload {
  meter: string;
  meter_number: string;
  current_reading: number;
  reading_date: string;
  reading_source: number;
  status: string;
  product_code: string;
}

interface PaymentSettings {
  outstanding: boolean;
  late_fees: boolean;
  credit_balance: boolean;
  installments: boolean;
}

interface ServiceCharges {
  [key: string]: string[];
}

interface Step3Data {
  reading_data: MeterReading[];
  payment_settings: PaymentSettings;
  service_charges: ServiceCharges;
}

const Step3ReadingsAndPayments: React.FC<Step3ReadingsAndPaymentsProps> = ({
  stepHelpers,
  currentStepIndex = 2,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  
  const step1DataFallback = stepHelpers?.getStepData(0);
  const planIdFallback = step1DataFallback?.selectedConsumer?.remotePlanId;
  const { data: planData } = usePlanData(planIdFallback);

  // State to store all step 3 data - initialize from stored data if available
  const [step3Data, setStep3Data] = useState<Step3Data>(() => {
    const storedStepData = stepHelpers?.getStepData(2);
    if (storedStepData) {
      // If we have stored data, use it
      return {
        reading_data: storedStepData.reading_data || [],
        payment_settings: storedStepData.payment_settings || {
          outstanding: true,
          late_fees: true,
          credit_balance: true,
          installments: true,
        },
        service_charges: storedStepData.service_charges || {},
      };
    }

    // Otherwise use default values
    return {
      reading_data: [],
      payment_settings: {
        outstanding: true,
        late_fees: true,
        credit_balance: true,
        installments: true,
      },
      service_charges: {},
    };
  });



  const transformedReadingData: MeterReadingPayload[] =
  step3Data.reading_data.map(({ meter_id, ...rest }) => ({
    ...rest,
    meter: meter_id,
  }));

  const handleContinue = () => {
    if (clearValidationError) {
      clearValidationError();
    }

    // Validate reading dates are within billing period
    const invalidReadings = step3Data.reading_data.filter((reading) => {
      if (!reading.reading_date || !invoiceStartDate || !invoiceEndDate) {
        return false;
      }

      const readingDate = new Date(reading.reading_date);
      const startDate = new Date(invoiceStartDate);
      const endDate = new Date(invoiceEndDate);

      // Set time to start of day for accurate comparison
      readingDate.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      return readingDate < startDate || readingDate > endDate;
    });

    if (invalidReadings.length > 0) {
      const meterNumbers = invalidReadings
        .map((r) => r.meter_number)
        .join(", ");
      toast({
        title: "Invalid Reading Dates",
        description: `Reading dates for meter(s) ${meterNumbers} are outside the billing period (${invoiceStartDate} to ${invoiceEndDate}). Please adjust the dates.`,
        variant: "destructive",
      });
      return;
    }

    // Prepare the final payload
    const payload = {
      reading_data: step3Data.reading_data,
      service_charges: step3Data.service_charges,
      payment_settings: step3Data.payment_settings,
      step: {
        step_working_on: 3,
      },
      async_mode: false,
      remoteUtilityId,
    };

    let finalServiceCharges = step3Data.service_charges;
    if (Object.keys(finalServiceCharges).length === 0 && planData?.serviceChargesForBilling) {
      const initialServiceCharges: Record<string, string[]> = {};
      ["fixedCharges", "surcharges", "taxes", "penalties"].forEach(catKey => {
        const arr = planData.serviceChargesForBilling[catKey];
        const snakeKey = catKey === "fixedCharges" ? "fixed_charges" : catKey;
        if (Array.isArray(arr)) {
          initialServiceCharges[snakeKey] = arr.map((c: any) => c.code || c.name);
        }
      });
      finalServiceCharges = initialServiceCharges;
    }

    const updatePayload = {
      reading_data: transformedReadingData,
      bill_data: {
        include_service_charge: finalServiceCharges,
        ...step3Data.payment_settings,
      },
      step: {
        step_working_on: 3,
      },
      generate_bill: true,
      remoteUtilityId,
    };

    console.log("debug - Final Payload:", JSON.stringify(updatePayload, null, 2));

    updateConsumer(updatePayload, {
      onSuccess: (res) => {
        stepHelpers.setStepData(3, {...res})
        // Save to stepHelpers
        if (stepHelpers) {
          stepHelpers.setStepData(currentStepIndex, {
            ...payload,
            isStepComplete: true,
            completedAt: new Date().toISOString(),
          });
        }

        // Navigate to next step
        if (onNext) {
          onNext();
        }
      },
    });
  };

  const handleBack = () => {
    if (onPrevious) {
      onPrevious();
    }
  };

  const consumerData = stepHelpers?.getStepData(0);

  const [isEditingPeriod, setIsEditingPeriod] = useState(false);
  const [billingEndDate, setBillingEndDate] = useState<string>(
    consumerData?.formData?.endDate
  );
  const [billingStartDate, setBillingStartDate] = useState<string>(
    consumerData?.formData?.startDate
  );

  const getDateValidation = (
    billingStartDate?: string,
    lastBillDateRaw?: string
  ) => {
    if (!billingStartDate || !lastBillDateRaw) return null;

    const startDate = new Date(billingStartDate);
    const lastBillDate = new Date(normalizeDate(lastBillDateRaw));

    startDate.setHours(0, 0, 0, 0);
    lastBillDate.setHours(0, 0, 0, 0);

    const expectedStartDate = new Date(lastBillDate);
    expectedStartDate.setDate(expectedStartDate.getDate() + 1);

    if (startDate <= lastBillDate) {
      return {
        type: "error",
        message: "Billing Start Date overlaps with the last billing period.",
      };
    }

    if (startDate > expectedStartDate) {
      return {
        type: "warning",
        message:
          "There is a gap between the last billing period end date and the selected start date.",
      };
    }

    return null;
  };

  const dateValidation = useMemo(() => {
    return getDateValidation(
      billingStartDate,
      consumerData?.consumerData?.lastBillDate
    );
  }, [billingStartDate, consumerData?.consumerData?.lastBillDate]);

  const billingDays = useMemo(() => {
    if (!billingStartDate || !billingEndDate) return 0;

    const start = new Date(billingStartDate);
    const end = new Date(billingEndDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const diffTime = end.getTime() - start.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    return diffDays >= 0 ? diffDays + 1 : 0;
  }, [billingStartDate, billingEndDate]);

  const getStep1Data = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(0);
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return null;
    }
  }, [stepHelpers]);

  const step1Data = getStep1Data();
  const consumerDataId = step1Data?.consumerData?.id;
  const planId = step1Data?.selectedConsumer?.remotePlanId;
  const consumerDataNo = step1Data?.consumerData?.accountNo;
  const invoiceStartDate = step1Data?.formData?.startDate;
  const invoiceEndDate = step1Data?.formData.endDate;
  const billId = step1Data?.billDraftId;

  const { mutateAsync: updateConsumer, isPending: isSubmitting } =
    useUpdateConsumerSelection(billId);

  // Memoized callback to update meter readings from child component
  const handleMeterReadingsUpdate = useCallback(
    (readingData: MeterReading[]) => {
      setStep3Data((prev) => ({
        ...prev,
        reading_data: readingData,
      }));
    },
    []
  );

  // Memoized callback to update payment settings from child component
  const handlePaymentSettingsUpdate = useCallback(
    (paymentSettings: PaymentSettings, serviceCharges: ServiceCharges) => {
      setStep3Data((prev) => ({
        ...prev,
        payment_settings: paymentSettings,
        service_charges: serviceCharges,
      }));
    },
    []
  );

  // Memoized tab components to prevent recreation on every render
  const tabComponents = useMemo(
    () => ({
      "meter-readings": {
        label: "Reading",
        component: (
          <MeterReadings
            consumerDataId={consumerDataId}
            invoiceStartDate={invoiceStartDate}
            invoiceEndDate={invoiceEndDate}
            consumerNo={consumerDataNo}
            meterReadingsUpdate={step3Data.reading_data}
            onMeterReadingsUpdate={handleMeterReadingsUpdate}
          />
        ),
      },
      payments: {
        label: "Payments & Service Charges",
        component: (
          <PaymentsServiceCharges
            planId={planId}
            consumerNo={consumerDataNo}
            paymentsUpdates={{
              ...step3Data.payment_settings,
              serviceCharges: step3Data.service_charges,
            }}
            onPaymentSettingsUpdate={handlePaymentSettingsUpdate}
          />
        ),
      },
    }),
    [
      consumerDataId,
      invoiceStartDate,
      invoiceEndDate,
      consumerDataNo,
      planId,
      step3Data.reading_data,
      step3Data.payment_settings,
      step3Data.service_charges,
      handleMeterReadingsUpdate,
      handlePaymentSettingsUpdate,
    ]
  );

  const handleBillingPeriodUpdate = () => {
    updateConsumer(
      {
        remoteUtilityId,
        startDate: billingStartDate,
        endDate: billingEndDate,
      },
      {
        onSuccess: () => {
          toast({ title: "Billing Period Updated Successfully" });
          stepHelpers?.setStepData(0, (prev: any) => ({
            ...prev,
            formData: {
              ...prev?.formData,
              startDate: billingStartDate,
              endDate: billingEndDate,
            },
          }));
        },
      }
    );
  };

  const handleDraft = () => {
    if (clearValidationError) {
      clearValidationError();
    }

    // Validate reading dates are within billing period
    const invalidReadings = step3Data.reading_data.filter((reading) => {
      if (!reading.reading_date || !invoiceStartDate || !invoiceEndDate) {
        return false;
      }

      const readingDate = new Date(reading.reading_date);
      const startDate = new Date(invoiceStartDate);
      const endDate = new Date(invoiceEndDate);

      // Set time to start of day for accurate comparison
      readingDate.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      return readingDate < startDate || readingDate > endDate;
    });

    if (invalidReadings.length > 0) {
      const meterNumbers = invalidReadings
        .map((r) => r.meter_number)
        .join(", ");
      toast({
        title: "Invalid Reading Dates",
        description: `Reading dates for meter(s) ${meterNumbers} are outside the billing period (${invoiceStartDate} to ${invoiceEndDate}). Please adjust the dates.`,
        variant: "destructive",
      });
      return;
    }

    // Prepare the final payload
    const payload = {
      reading_data: step3Data.reading_data,
      service_charges: step3Data.service_charges,
      payment_settings: step3Data.payment_settings,
      step: {
        step_working_on: 3,
      },
      async_mode: false,
      remoteUtilityId,
    };

    let finalServiceCharges = step3Data.service_charges;
    if (Object.keys(finalServiceCharges).length === 0 && planData?.serviceChargesForBilling) {
      const initialServiceCharges: Record<string, string[]> = {};
      ["fixedCharges", "surcharges", "taxes", "penalties"].forEach(catKey => {
        const arr = planData.serviceChargesForBilling[catKey];
        const snakeKey = catKey === "fixedCharges" ? "fixed_charges" : catKey;
        if (Array.isArray(arr)) {
          initialServiceCharges[snakeKey] = arr.map((c: any) => c.code || c.name);
        }
      });
      finalServiceCharges = initialServiceCharges;
    }

    const updatePayload = {
      reading_data: transformedReadingData,
      bill_data: {
        include_service_charge: finalServiceCharges,
        ...step3Data.payment_settings,
      },
      step: {
        step_working_on: 3,
      },
      generate_bill: true,
      remoteUtilityId,
    };

    console.log("debug - Final Payload:", JSON.stringify(updatePayload, null, 2));

    updateConsumer(updatePayload, {
      onSuccess: (res) => {
        stepHelpers.setStepData(3, {...res})
        // Save to stepHelpers
        if (stepHelpers) {
          stepHelpers.setStepData(currentStepIndex, {
            ...payload,
            isStepComplete: true,
            completedAt: new Date().toISOString(),
          });
        }

        // Navigate to next step
        navigate('/bx/single-bill');
      },
    });
  };
  return (
    <div className="space-y-6">
      <Card className="border-gray-200 shadow-sm bg-white">
        <CardContent className="p-6">
          {isEditingPeriod ? (
            <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <Settings className="h-4 w-4 text-blue-600" />
                  Edit Billing Period
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingPeriod(false)}
                  className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-700">
                    Billing Period Start
                  </Label>
                  <Input
                    type="date"
                    value={billingStartDate}
                    onChange={(e) => setBillingStartDate(e.target.value)}
                    className={cn(
                      "bg-gray-50/50 focus:bg-white transition-colors",
                      dateValidation?.type === "error"
                        ? "border-red-300 focus:border-red-500"
                        : dateValidation?.type === "warning"
                        ? "border-amber-300 focus:border-amber-500"
                        : ""
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-700">
                    Billing Period End
                  </Label>
                  <Input
                    type="date"
                    value={billingEndDate}
                    onChange={(e) => setBillingEndDate(e.target.value)}
                    min={billingStartDate}
                    className="bg-gray-50/50 focus:bg-white transition-colors"
                  />
                </div>
              </div>

              {dateValidation && (
                <div
                  className={cn(
                    "flex items-start gap-2 text-xs p-2 rounded-md",
                    dateValidation.type === "error"
                      ? "bg-red-50 text-red-700 border border-red-100"
                      : "bg-amber-50 text-amber-700 border border-amber-100"
                  )}
                >
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>{dateValidation.message}</span>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingPeriod(false)}
                  className="h-8 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleBillingPeriodUpdate()}
                  className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                >
                  Update Period
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0 border border-blue-100">
                  <CalendarIcon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">
                    Billing Period Configuration
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="font-medium">
                        {billingStartDate ? billingStartDate : "N/A"}
                      </span>
                      <span className="text-gray-400">to</span>
                      <span className="font-medium">
                        {billingEndDate ? billingEndDate : "N/A"}
                      </span>
                    </div>
                    <Badge
                      variant="secondary"
                      className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none"
                    >
                      {billingDays} Days
                    </Badge>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditingPeriod(true)}
                className="shrink-0 gap-2"
              >
                <Settings className="h-3.5 w-3.5" />
                Edit Billing Period
              </Button>
            </div>
          )}

          {!isEditingPeriod && (
            <div className="mt-4 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-3 rounded-md border border-amber-100">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <p>
                The billing period can be adjusted if the new current reading is
                outside the defined billing period.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      <Tabs
        defaultValue="meter-readings"
        tabComponents={tabComponents}
        tabsListClassName="grid grid-cols-2 gap-2"
        className="space-y-4"
        disableUrlSync={true}
      />
      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isValidating || isSubmitting}
        >
          Previous
        </Button>
        <div>
          <Button
            onClick={handleDraft}
            disabled={isValidating || isSubmitting}
            className="min-w-[200px] mr-5"
          >
            {isValidating || isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                {"Saving..."}
              </>
            ) : (
              "Save As Draft"
            )}
          </Button>
          <Button
            onClick={handleContinue}
            disabled={isValidating || isSubmitting}
            className="min-w-[200px]"
          >
            {isValidating || isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                {"Processing..."}
              </>
            ) : (
              "Continue to Review"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Step3ReadingsAndPayments;
